<template>
    <div id="allPortfoliosSummary">
        <div class="border border-t-0 mt-4 mb-0" :class="{'border-b-0' : haveItems}">
            <div class="flex flex-wrap bg-tan-200 border-t-2 border-blue-600 px-3 pt-2 md:pb-0 pb-2">
                <div class="inline-block mr-4 mb-2">
                    <select-c label="Period:" :options="periods" v-model="period" @change="onChangePeriod"></select-c>
                </div>
                <div class="inline-block mr-2 mt-2 xs:mb-2 lg:my-0 sm:ml-auto">
                    <!-- <div class='inline-block pt-1 mb-2'> -->
                    <span class='text-sm leading-7'>
                        As Of {{ getCurrentDate() }}
                    </span>
                </div>
                <div class="inline-block mt-2 xs:mb-2 lg:my-0 print:hidden" v-if="haveItems">
                    <span class="btn btn-sm btn-green" @click="exportToCSV">
                        <i class="fas fa-file-csv"></i>&nbsp;Export
                    </span>
                </div>
            </div>
        </div>

        <alert-box v-if="!haveItems">
            <template #body>
                No portfolios.
            </template>
        </alert-box>

        <div class="table-responsive" v-if="haveItems">
            <table class="table table-striped table-hover border trade-tracker-table-quotes">
                <thead>
                    <tr>
                        <th>Portfolio Name</th>
                        <th>Account Value</th>
                        <th>Available Cash</th>
                        <th>Today's Return</th>
                        <th>Active Return</th>
                        <th>Closed Return</th>
                        <th>Net Return</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="portfolio in summaryData.portfolios">
                        <tr>
                            <td><a @click.prevent="onClickPortfolio(portfolio.id)" class="cursor-pointer">{{ portfolio.caption }}</a></td>
                            <td :class="getClass(portfolio.totals.raw.account_value)">{{ portfolio.totals.account_value || '-' }}</td>
                            <td>
                                <template v-if="portfolio.adjust_cash">
                                <span :class="getClass(portfolio.totals.raw.available_cash)">{{ portfolio.totals.available_cash || '-' }}</span>
                                </template>
                                <template v-else>N/A</template>
                            </td>
                            <td :class="getClass(portfolio.totals.raw.daily_net_return)">{{ portfolio.totals.daily_net_return || '-' }}</td>
                            <td :class="getClass(portfolio.totals.raw.active_return)">{{ portfolio.totals.active_return || '-' }}</td>
                            <td :class="getClass(portfolio.totals.raw.closed_return)">{{ portfolio.totals.closed_return || '-' }}</td>
                            <td :class="getClass(portfolio.totals.raw.net_return)">{{ portfolio.totals.net_return || '-' }}</td>
                        </tr>
                    </template>
                </tbody>
                <tfoot>
                    <tr>
                        <th>Portfolio Name</th>
                        <th>Account Value</th>
                        <th>Available Cash</th>
                        <th>Today's Return</th>
                        <th>Active Return</th>
                        <th>Closed Return</th>
                        <th>Net Return</th>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    summaryData: Object,
    defaultPeriod: String,
});

const emit = defineEmits(['changePeriod', 'changePortfolio']);

const period = ref(props.defaultPeriod);
const periods = [
    {
        label: 'Intraday',
        value: 'intraday',
        default: props.defaultPeriod === 'intraday'
    },
    {
        label: 'End-of-Day',
        value: 'eod',
        default: props.defaultPeriod === 'eod'
    }
];

const haveItems = computed(() => props.summaryData.portfolios && props.summaryData.portfolios.length > 0);

const exportToCSV = () => {
    // Helper function to quote fields containing special characters
    const quoteField = (field) => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
            return `"${field.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
        }
        return field;
    };

    const sPeriod = period.value === 'eod' ? 'End-of-Day' : 'Intraday';

    // CSV Header
    const headers = [
        'Portfolio Name', 'Account Value', 'Available Cash', 'Today\'s Return', 'Active Return', 'Closed Return', 'Net Return'
    ];

    // Prepare rows for CSV export
    const rows = [];

    props.summaryData.portfolios.forEach((item) => {
        const mainRow = [
            quoteField(item.caption || '-'),
            quoteField(item.totals.account_value || '-'),
            quoteField(item.adjust_cash ? item.totals.available_cash || '-' : 'N/A'),
            quoteField(item.totals.daily_net_return || '-'),
            quoteField(item.totals.active_return || '-'),
            quoteField(item.totals.closed_return || '-'),
            quoteField(item.totals.net_return || '-')
        ];
        rows.push(mainRow);
    });

    // Combine headers and rows into CSV format
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");

    // Create a Blob for the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a link to download the CSV file
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Trade Tracker Summary (${sPeriod}).csv`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

watch(() => props.defaultPeriod, (newVal) => {
    period.value = newVal;
});

const getCurrentDate = () => {
    return (new Date()).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const onChangePeriod = () => {
    emit('changePeriod', period.value);
};

const onClickPortfolio = (portfolioId) => {
    emit('changePortfolio', portfolioId);
};

// const getItemField = (item, field) => {
//     if (period.value === 'eod') {
//         return item['daily' + field.charAt(0).toUpperCase() + field.slice(1)];
//     } else {
//         return item[field];
//     }
// };

const getClass = (value) => {
    if (typeof (value) === 'string') {
        value = value.replace(/\$|,/g, '');
    }
    if (value > 0) {
        return 'text-up';
    } else if (value < 0) {
        return 'text-down';
    } else if (value === 0) {
        return 'text-unch';
    }
};
</script>

<style lang="scss">
#allPortfoliosSummary {
    .trade-tracker-table {
        &.table {
            thead,
            tfoot {
                td, th {
                    @apply px-3 py-2;
                }
            }
            thead {
                tr {
                    @apply border-t;
                    td, th {
                        @apply text-center;
                    }
                }
            }
            tbody {
                td, th {
                    @apply px-3;
                    &:not(.portfolio-item-notes) {
                        @apply text-center;
                    }
                }
            }
            .sortable {
                @apply cursor-pointer;
            }
        }
    }

    .trade-tracker-table-quotes {
        &.table {
            thead,
            tfoot {
                td, th {
                    @apply px-3 py-2;
                }
            }
            thead {
                tr {
                    @apply border-t;
                    td, th {
                    }
                }
            }
            tbody {
                td, th {
                    @apply px-3
                }
            }

            td, th {
                @apply px-3 text-right;
                &:first-child {
                    @apply text-left;
                }
            }

            .sortable {
                @apply cursor-pointer;
            }
        }
    }
}
</style>
